import { GetPostPaymentRequestsParams } from '@round/api';
import { DomainFiltersProps, getInitialFilters } from 'ui-new/Filters/helpers';
import { Filter } from 'ui-new/Filters/useFilterManager';
import { ReactComponent as PlanIcon } from 'assets/Document.svg';
import { ReactComponent as UserIcon } from 'assets/icons/User.svg';
import { usePlanSelect } from 'Modules/Advertising/InfluencerPlan/hooks/usePlanSelect';
import Dropdown from 'ui-new/Dropdown/Dropdown';
import FilterBadgeClearable from 'ui/Filters/FilterBadge/FilterBadgeClearable/FilterBadgeClearable';
import FilterHeader from 'ui/Filters/FilterHeader/FilterHeader';
import { Select } from '@round/ui-kit';
import InfluencerPlanBadge from 'Modules/Advertising/InfluencerPlan/components/InfluencerPlanBadge/InfluencerPlanBadge';
import { StylesConfig, ValueType } from 'react-select';
import { InfluencerPlanOption } from 'Modules/Advertising/InfluencerPlan/components/InfluencerPlanSelectOption/InfluencerPlanSelectOption';
import styles from './OutstandingPaymentFilters.module.css';
import Filters from 'ui-new/Filters/Filters';
import { getNonEmptyKeys } from 'utility/utility';
import TiktokInfluencerFilter from 'Modules/TikTok/components/filters/TiktokInfluencerFilter/TiktokInfluencerFilter';

export type OutstandingPaymentsFiltersParams = Pick<
    GetPostPaymentRequestsParams,
    'plan_id' | 'tiktok_influencer_user_id'
>;

type Props = DomainFiltersProps<OutstandingPaymentsFiltersParams>;

const selectStyles: StylesConfig = {
    menu: (provided) => ({
        ...provided,
        position: 'unset',
        boxShadow: 'none',
    }),
    control: (provided) => ({
        ...provided,
        borderRadius: '0.75rem',
        border: '1px solid #D1D5DB',
        cursor: 'text',
    }),
};

const OutstandingPaymentsFilters = ({ value, onChange }: Props) => {
    const planSelectProps = usePlanSelect({ initialPlanIdData: value.plan_id, isMulti: false });

    const filters: Filter<keyof OutstandingPaymentsFiltersParams>[] = [
        {
            name: 'plan_id',
            label: 'Plan',
            icon: PlanIcon,
            render(this, helpers) {
                const Icon = this.icon;

                return (
                    <Dropdown
                        isOpen={helpers.isOpen}
                        onClose={helpers.close}
                        control={
                            <FilterBadgeClearable
                                onClick={helpers.open}
                                onClear={() => {
                                    planSelectProps.onChange(null);
                                    onChange({ plan_id: undefined });
                                    helpers.remove();
                                }}
                            >
                                <div>
                                    <span>{this.label}: </span>
                                    <span>{planSelectProps.value?.label || 'None'}</span>
                                </div>
                            </FilterBadgeClearable>
                        }
                    >
                        <FilterHeader
                            name={this.label}
                            icon={<Icon className={styles.filterIcon} />}
                            hasValue={!!planSelectProps.value}
                            onReset={() => {
                                planSelectProps.onChange(null);
                                onChange({ plan_id: undefined });
                            }}
                        />

                        <Select
                            {...planSelectProps}
                            onChange={(value: ValueType<InfluencerPlanOption, false>) => {
                                planSelectProps.onChange(value);
                                onChange({ plan_id: value?.value });
                            }}
                            styles={selectStyles}
                            className={styles.filter}
                            menuIsOpen={!!planSelectProps.inputValue.length}
                            menuPortalTarget={null}
                            placeholder="Search plan"
                            components={{
                                IndicatorSeparator: null,
                                DropdownIndicator: null,
                            }}
                            formatOptionLabel={(option: InfluencerPlanOption) => (
                                <InfluencerPlanBadge
                                    planInfo={{
                                        releaseId: option.releaseId ?? undefined,
                                        releaseName: option.label ?? '',
                                        artistName: option.artistName ?? '',
                                        imageUrl: option.imageUrl ?? '',
                                    }}
                                />
                            )}
                            controlShouldRenderValue={false}
                        />
                    </Dropdown>
                );
            },
        },
        {
            name: 'tiktok_influencer_user_id',
            label: 'TikTok Creator',
            icon: UserIcon,
            render(this, helpers) {
                const Icon = this.icon;

                return (
                    <TiktokInfluencerFilter
                        isOpen={helpers.isOpen}
                        onOpen={helpers.open}
                        onClose={helpers.close}
                        name={this.label}
                        icon={<Icon className={styles.filterIcon} />}
                        initialInfluencerUserId={value.tiktok_influencer_user_id}
                        onChange={(val) => onChange({ tiktok_influencer_user_id: val?.value })}
                        onClear={() => onChange({ tiktok_influencer_user_id: undefined })}
                        onRemove={() => {
                            onChange({ tiktok_influencer_user_id: undefined });
                            helpers.remove();
                        }}
                    />
                );
            },
        },
    ];

    return (
        <Filters
            sessionKey="round-outstanding-payments-filters"
            filters={filters}
            options={{ initialFilters: (state) => getInitialFilters(state, getNonEmptyKeys(value)) }}
        />
    );
};

export default OutstandingPaymentsFilters;
